import { ReactNode, useEffect, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  MouseSensor,
  DragStartEvent,
  DragEndEvent,
  pointerWithin,
} from '@dnd-kit/core'
import { useAddReleaseToFolder } from '../../hooks/useFolders'
import { useToast } from '../../context/ToastContext'
import { Release } from '../../types/release'
import styles from './DnDProvider.module.css'

const DND_BODY_CLASS = 'dnd-dragging'

interface DnDProviderProps {
  children: ReactNode
}

export function DnDProvider({ children }: DnDProviderProps) {
  const [activeRelease, setActiveRelease] = useState<Release | null>(null)
  const addToFolder = useAddReleaseToFolder()
  const { showToast } = useToast()

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  // Freeze scroll while dragging. Without this the page jumps around due
  // to ambient wheel/touch events landing on scroll containers below the
  // drag overlay. dnd-kit's own autoscroll is disabled via the autoScroll
  // prop below — this covers user-initiated scroll.
  useEffect(() => {
    if (!activeRelease) return
    document.body.classList.add(DND_BODY_CLASS)
    const prevent = (e: Event) => { e.preventDefault() }
    // passive: false so preventDefault actually blocks the scroll
    document.addEventListener('wheel', prevent, { passive: false })
    document.addEventListener('touchmove', prevent, { passive: false })
    return () => {
      document.body.classList.remove(DND_BODY_CLASS)
      document.removeEventListener('wheel', prevent)
      document.removeEventListener('touchmove', prevent)
    }
  }, [activeRelease])

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    if (active?.data?.current?.release) {
      setActiveRelease(active.data.current.release)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveRelease(null)

    if (!over) return

    // Check if we dropped on a folder
    const overId = over.id as string
    if (overId.startsWith('folder-')) {
      const folderId = parseInt(overId.replace('folder-', ''), 10)
      const release = active?.data?.current?.release as Release | undefined

      if (!release || !folderId) return

      try {
        await addToFolder.mutateAsync({
          folderId,
          releaseId: release.id,
        })
        showToast(`Added "${release.title}" to folder`, 'success')
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error || 'Failed to add release to folder'
        showToast(errorMessage, 'error')
      }
    }
  }

  const handleDragCancel = () => {
    setActiveRelease(null)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      autoScroll={false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children}
      <DragOverlay>
        {activeRelease && (
          <div className={styles.dragOverlay}>
            {activeRelease.images?.[0] ? (
              <img
                src={activeRelease.images[0].uri || activeRelease.images[0].uri150}
                alt={activeRelease.title}
                className={styles.dragImage}
              />
            ) : (
              <div className={styles.dragNoImage}>
                <i className="bi bi-disc"></i>
              </div>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
